import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, ApiError } from './client';

/**
 * NPS API client + query hooks. Kept self-contained (separate from the large
 * shared queries.ts / types.ts) so the feature is easy to review and revert.
 */

export type Audience = 'caregiver' | 'client' | 'referral';

export const AUDIENCE_LABEL: Record<Audience, string> = {
  caregiver: 'Caregivers',
  client: 'Clients',
  referral: 'Referral sources',
};

export type NpsSummary = {
  responses: number;
  promoters: number;
  passives: number;
  detractors: number;
  nps: number | null;
  belowThreshold: boolean;
};

export type AudienceSummary = NpsSummary & { audience: Audience; label: string };

// ── Public respondent endpoints (no auth) ─────────────
export type PublicLinkInfo = {
  audience: Audience;
  locationName: string;
  question: { prompt: string; lowLabel: string; highLabel: string; commentPrompt: string };
  alreadySubmitted: boolean;
};

export async function fetchPublicLink(token: string): Promise<PublicLinkInfo> {
  const res = await fetch(`/api/nps-public/${encodeURIComponent(token)}`, {
    credentials: 'include',
  });
  if (!res.ok) {
    let detail = `${res.status}`;
    try {
      const b = (await res.json()) as { error?: string };
      if (b.error) detail = b.error;
    } catch {}
    throw new ApiError(detail, res.status);
  }
  return res.json() as Promise<PublicLinkInfo>;
}

export async function submitPublicNps(
  token: string,
  score: number,
  comment?: string,
): Promise<void> {
  const res = await fetch(`/api/nps-public/${encodeURIComponent(token)}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ score, comment: comment || undefined }),
  });
  if (!res.ok) {
    let detail = `${res.status}`;
    try {
      const b = (await res.json()) as { error?: string };
      if (b.error) detail = b.error;
    } catch {}
    throw new ApiError(detail, res.status);
  }
}

// ── Franchisee: share links ───────────────────────────
export type NpsLink = { audience: Audience; label: string; token: string | null };
export type MyLinksResponse = { af: string; links: NpsLink[] };

export function useNpsLinksQuery(af: string | null) {
  return useQuery<MyLinksResponse>({
    queryKey: ['nps', 'my-links', af],
    queryFn: () => apiGet<MyLinksResponse>(`/api/nps/my-links?af=${encodeURIComponent(af!)}`),
    enabled: !!af,
    staleTime: 5 * 60 * 1000,
  });
}

export function useRotateNpsLink(af: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (audience: Audience) =>
      apiPost<{ audience: Audience; token: string }>(
        `/api/nps/my-links/${audience}/rotate?af=${encodeURIComponent(af!)}`,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['nps', 'my-links', af] }),
  });
}

// ── Franchisee: results ───────────────────────────────
export type NpsComment = {
  audience: Audience;
  label: string;
  score: number;
  bucket: 'promoter' | 'passive' | 'detractor';
  comment: string;
  createdAt: string;
};
export type NpsResults = {
  af: string;
  overall: NpsSummary;
  byAudience: AudienceSummary[];
  comments: NpsComment[];
};

export function useNpsResultsQuery(af: string | null) {
  return useQuery<NpsResults>({
    queryKey: ['nps', 'results', af],
    queryFn: () => apiGet<NpsResults>(`/api/nps/results?af=${encodeURIComponent(af!)}`),
    enabled: !!af,
    staleTime: 5 * 60 * 1000,
  });
}

// ── Admin: network summary ────────────────────────────
export type NpsMapPoint = {
  afNumber: string;
  name: string;
  location: string;
  territory: string | null;
  state: string | null;
  fbcName: string | null;
  lat: number;
  lng: number;
  responses: number;
  nps: number | null;
  belowThreshold: boolean;
};
export type NpsGroupAgg = NpsSummary & { key: string; label: string };
export type NpsNetworkSummary = {
  minResponsesToShow: number;
  mapPoints: NpsMapPoint[];
  byAudience: AudienceSummary[];
  byTerritory: NpsGroupAgg[];
  byRegion: NpsGroupAgg[];
  byState: NpsGroupAgg[];
  locationsWithResponses: number;
};

export function useNpsNetworkSummaryQuery(enabled: boolean) {
  return useQuery<NpsNetworkSummary>({
    queryKey: ['nps', 'network-summary'],
    queryFn: () => apiGet<NpsNetworkSummary>('/api/nps/admin/network-summary'),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/** NPS → color. Diverging: strong green (high) → amber (mid) → red (low). */
export function npsColor(nps: number | null): string {
  if (nps == null) return '#cbd5e1';
  if (nps >= 70) return '#15803d';
  if (nps >= 50) return '#22c55e';
  if (nps >= 30) return '#84cc16';
  if (nps >= 0) return '#f59e0b';
  if (nps >= -30) return '#f97316';
  return '#dc2626';
}
